import type { FieldDataProvider } from '@/domains/iot-v2/normalized-store';
import { apiGet, requestApi } from './client';
import type { ApiResult } from './types';
/**
 * 알람 API 응답 타입
 */
export interface AlarmApiResponse {
  key: string;
  type: string;
  event: string;
  field_uid: string;
  device_uid?: string | null;
  device_name?: string | null;
  title: string;
  content: string | null;
  img_url?: string | null;
  is_check: boolean;
  popup_enabled?: boolean;
  receive_at: string;
  type_text: string;
  level_text: string;
  meta?: Record<string, unknown> & {
    alarm_data?: Record<string, unknown>;
    alarmData?: Record<string, unknown>;
    value?: Record<string, unknown>;
    source?: string;
    threshold?: Record<string, unknown>;
  };
}
export type AlarmSortField = 'created_at' | 'closed_at' | 'message_type' | 'category' | 'display_label';
export type AlarmSortDirection = 'asc' | 'desc';
export type AlarmCheckStatus = 'checked' | 'unchecked';
export interface AlarmListQueryParams {
  provider?: FieldDataProvider;
  page?: number;
  per_page?: number;
  field_uid?: string;
  category?: string;
  search_word?: string;
  start_date?: string;
  end_date?: string;
  sort_by?: AlarmSortField;
  sort_dir?: AlarmSortDirection;
  check_status?: AlarmCheckStatus;
  current_only?: boolean;
  exclude_command_delivery?: boolean;
}
function appendAlarmListQuery(searchParams: URLSearchParams, params: AlarmListQueryParams) {
  Object.entries({
    page: params.page,
    per_page: params.per_page,
    field_uid: params.field_uid,
    category: params.category,
    search_word: params.search_word,
    start_date: params.start_date,
    end_date: params.end_date,
    sort_by: params.sort_by,
    sort_dir: params.sort_dir,
    check_status: params.check_status,
    current_only: params.current_only,
    exclude_command_delivery: params.exclude_command_delivery,
  }).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      return;
    }
    searchParams.set(key, String(value));
  });
}
/**
 * 알람 Enum 항목 타입
 */
export interface AlarmEnumItem {
  label: string;
  value: string;
  color: string;
  bg_color: string;
  counts: number;
}
/**
 * 장비 타입별 개수 응답
 */
export interface AlarmCountByTypeResponse {
  counts: AlarmEnumItem[];
}
/**
 * 알람 Enum 응답을 배열 형태로 정규화합니다.
 */
export function normalizeAlarmEnumItems(
  data:
    | AlarmEnumItem[]
    | {
        items: AlarmEnumItem[];
      }
    | {
        data: AlarmEnumItem[];
      }
    | {
        alrams_enums: AlarmEnumItem[];
      }
    | {
        alarms_enums: AlarmEnumItem[];
      }
    | unknown
): AlarmEnumItem[] {
  if (Array.isArray(data)) {
    return data as AlarmEnumItem[];
  }
  if (!data || typeof data !== 'object') {
    return [];
  }
  const objectData = data as Record<string, unknown>;
  if (Array.isArray(objectData.items)) {
    return objectData.items as AlarmEnumItem[];
  }
  if (Array.isArray(objectData.data)) {
    return objectData.data as AlarmEnumItem[];
  }
  if (Array.isArray(objectData.alrams_enums)) {
    return objectData.alrams_enums as AlarmEnumItem[];
  }
  if (Array.isArray(objectData.alarms_enums)) {
    return objectData.alarms_enums as AlarmEnumItem[];
  }
  if (objectData.data && typeof objectData.data === 'object') {
    const nestedData = objectData.data as Record<string, unknown>;
    if (Array.isArray(nestedData.items)) {
      return nestedData.items as AlarmEnumItem[];
    }
  }
  for (const value of Object.values(objectData)) {
    if (Array.isArray(value)) {
      return value as AlarmEnumItem[];
    }
  }
  return [];
}
/**
 * 알람 목록 API 응답 타입입니다.
 */
export interface AlarmListApiResponse {
  page: number;
  per_page: number;
  total_pages: number;
  total_items: number;
  items: AlarmApiResponse[];
}
type AlarmEnumsApiResponse =
  | AlarmEnumItem[]
  | {
      items: AlarmEnumItem[];
    }
  | {
      data: AlarmEnumItem[];
    }
  | {
      alrams_enums: AlarmEnumItem[];
    }
  | {
      alarms_enums: AlarmEnumItem[];
    };
const alarmEnumsRequestCache = new Map<string, ReturnType<typeof apiGet<AlarmEnumsApiResponse>>>();
/**
 * /alarms 에서 알람 목록을 조회합니다 (프록시가 /api/v1/ 을 자동으로 추가합니다)
 */
export async function fetchAlarmList(params: AlarmListQueryParams) {
  return apiGet<AlarmListApiResponse>({
    version: 'V2',
    path: '/alarms',
    query: {
      ...{ preview: true },
      page: params.page ?? 1,
      per_page: params.per_page ?? 100,
      ...(params.field_uid && { field_uid: params.field_uid }),
      ...(params.category && { category: params.category }),
      ...(params.search_word && { search_word: params.search_word }),
      ...(params.start_date && { start_date: params.start_date }),
      ...(params.end_date && { end_date: params.end_date }),
      ...(params.sort_by && { sort_by: params.sort_by }),
      ...(params.sort_dir && { sort_dir: params.sort_dir }),
      ...(params.check_status && { check_status: params.check_status }),
      ...(params.current_only !== undefined && { current_only: params.current_only }),
      ...(params.exclude_command_delivery !== undefined && { exclude_command_delivery: params.exclude_command_delivery }),
    },
  });
}
/** 현장의 미해제 알람을 모든 페이지에서 조회합니다. */
export async function fetchAllActiveAlarms(fieldUid: string, perPage = 500, provider?: FieldDataProvider): Promise<ApiResult<AlarmListApiResponse>> {
  const firstResult = await fetchAlarmList({
    check_status: 'unchecked',
    current_only: true,
    provider,
    field_uid: fieldUid,
    page: 1,
    per_page: perPage,
    sort_by: 'created_at',
    sort_dir: 'desc',
  });
  if (!firstResult.ok) return firstResult;
  const totalPages = Math.max(1, firstResult.data.total_pages || 1);
  if (totalPages <= 1) return firstResult;
  // 알람이 많아도 요청을 한꺼번에 생성하지 않고 같은 조건으로 순차 조회합니다.
  const items = [...firstResult.data.items];
  for (let page = 2; page <= totalPages; page += 1) {
    const result = await fetchAlarmList({
      check_status: 'unchecked',
      current_only: true,
      provider,
      field_uid: fieldUid,
      page,
      per_page: perPage,
      sort_by: 'created_at',
      sort_dir: 'desc',
    });
    if (!result.ok) return result;
    items.push(...result.data.items);
  }
  return {
    ...firstResult,
    data: {
      ...firstResult.data,
      items,
    },
  };
}
/**
 * /alarms/enums 에서 알람 타입 Enum 목록을 조회합니다
 * 응답은 배열 또는 {items: AlarmEnumItem[]} 또는 {alrams_enums: AlarmEnumItem[]} 구조일 수 있음
 */
export async function fetchAlarmEnums(params?: { provider?: FieldDataProvider; field_uid?: string; current_only?: boolean }) {
  const cacheKey = `${params?.provider ?? 'V2'}:${params?.field_uid?.trim() || '__all__'}:${params?.current_only === true}`;
  const cachedRequest = alarmEnumsRequestCache.get(cacheKey);
  if (cachedRequest) {
    return cachedRequest;
  }
  const request = apiGet<AlarmEnumsApiResponse>({
    version: 'V2',
    path: '/alarms/enums',
    query: {
      ...{ preview: true },
      ...(params?.field_uid && { field_uid: params.field_uid }),
      ...(params?.current_only !== undefined && { current_only: params.current_only }),
    },
  }).finally(() => {
    alarmEnumsRequestCache.delete(cacheKey);
  });
  alarmEnumsRequestCache.set(cacheKey, request);
  return request;
}
/**
 * 단건 알람의 확인 여부를 갱신합니다.
 */
export async function updateAlarmCheck(params: { provider?: FieldDataProvider; field_uid: string; target_uid: string; is_check: boolean }) {
  return requestApi<{
    closed_at: string | null;
    message: string;
  }>({
    body: {
      is_check: params.is_check,
    },
    method: 'PATCH',
    version: 'V2',
    path: `/alarms/${params.field_uid}/${params.target_uid}/check`,
  });
}
/**
 * 현재 미확인 알람 전체를 확인 처리합니다.
 */
export async function updateAlarmCheckAll(params?: { provider?: FieldDataProvider; field_uid?: string; category?: string }) {
  return requestApi<{
    closed_at: string | null;
    message: string;
    updated_count: number;
  }>({
    method: 'PATCH',
    version: 'V2',
    path: '/alarms/check-all',
    query: {
      ...(params?.field_uid && { field_uid: params.field_uid }),
      ...(params?.category && { category: params.category }),
    },
  });
}
export async function fetchAlarmOnlyList(params: AlarmListQueryParams) {
  return apiGet<AlarmListApiResponse>({
    version: 'V2',
    path: '/alarms/alarm',
    query: {
      ...{ preview: true },
      page: params.page ?? 1,
      per_page: params.per_page ?? 100,
      ...(params.field_uid && { field_uid: params.field_uid }),
      ...(params.category && { category: params.category }),
      ...(params.search_word && { search_word: params.search_word }),
      ...(params.start_date && { start_date: params.start_date }),
      ...(params.end_date && { end_date: params.end_date }),
      ...(params.sort_by && { sort_by: params.sort_by }),
      ...(params.sort_dir && { sort_dir: params.sort_dir }),
      ...(params.check_status && { check_status: params.check_status }),
    },
  });
}
/**
 * 종합 알림목록 엑셀 다운로드 URL을 생성합니다.
 */
export function buildAlarmOnlyListExcelUrl(params: AlarmListQueryParams) {
  const searchParams = new URLSearchParams();
  appendAlarmListQuery(searchParams, params);
  searchParams.set('preview', 'true');
  searchParams.delete('page');
  searchParams.delete('per_page');
  const queryString = searchParams.toString();
  const base = '/api/v2/alarms/alarm/excel';
  return queryString ? `${base}?${queryString}` : base;
}
